import React from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../Modal';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import './styles.css';

const ModalEditCustomResponse = ({ isOpen, onClose, onSubmit, initialData }) => {
    const { t } = useTranslation('botConfig');

    const validationSchema = Yup.object({
        question: Yup.string()
            .required(t('validation.questionRequired'))
            .min(3, t('validation.questionMinLength'))
            .max(100, t('validation.questionMaxLength')),
        response: Yup.string()
            .required(t('validation.responseRequired'))
            .min(10, t('validation.responseMinLength'))
            .max(300, t('validation.responseMaxLength'))
    });

    const PREFIX_TEXT = t('addModal.prefix');

    const getQuestionWithoutPrefix = (question) => {
        if (question && question.startsWith(PREFIX_TEXT)) {
            return question.substring(PREFIX_TEXT.length);
        }
        return question || '';
    };

    const initialValues = {
        question: getQuestionWithoutPrefix(initialData?.question),
        response: initialData?.response || ''
    };

    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            const finalValues = {
                ...values,
                question: PREFIX_TEXT + values.question
            };
            await onSubmit(finalValues, { setSubmitting });
        } catch (error) {
            setSubmitting(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <div className="edit-response-modal">
                <div className="edit-response-modal-header">
                    <h2 className="edit-response-modal-title">{t('editModal.title')}</h2>
                    <p className="edit-response-modal-subtitle">{t('addModal.description')}</p>
                </div>

                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    enableReinitialize
                >
                    {({ isSubmitting, values }) => (
                        <Form className="edit-response-form">
                            <div className="edit-response-field-group">
                                <label htmlFor="question" className="edit-response-field-label">
                                    {t('addModal.whenToUseLabel')}
                                </label>
                                <div className="edit-response-question-wrapper">
                                    <span className="edit-response-prefix">{PREFIX_TEXT}</span>
                                    <Field
                                        as="textarea"
                                        id="question"
                                        name="question"
                                        placeholder={t('addModal.questionPlaceholder')}
                                        className="edit-response-question-input"
                                        rows="3"
                                    />
                                </div>
                                <ErrorMessage name="question" component="div" className="edit-response-error" />
                            </div>

                            <div className="edit-response-field-group">
                                <label htmlFor="response" className="edit-response-field-label">
                                    {t('addModal.instructionsLabel')}
                                </label>
                                <Field
                                    as="textarea"
                                    id="response"
                                    name="response"
                                    placeholder={t('addModal.responsePlaceholder')}
                                    className="edit-response-textarea"
                                    rows="6"
                                />
                                <ErrorMessage name="response" component="div" className="edit-response-error" />
                                <div className="edit-response-char-count">
                                    {values.response.length}/300
                                </div>
                            </div>

                            <div className="edit-response-actions">
                                <button
                                    type="button"
                                    className="edit-response-btn edit-response-btn-cancel"
                                    onClick={onClose}
                                    disabled={isSubmitting}
                                >
                                    {t('addModal.cancel')}
                                </button>
                                <button
                                    type="submit"
                                    className="edit-response-btn edit-response-btn-submit"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? t('editModal.updating') : t('editModal.update')}
                                </button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </Modal>
    );
};

export default ModalEditCustomResponse;
